/**
 * Preview for a photo within a critique session
 * Displays: critique session hero image (left) + specific photo (right)
 *
 * @module pages/tasks/components/previews/PhotoInCritiqueTargetPreview
 */

import { useQuery } from '@apollo/client';
import { usePhotoByIdGraphQL } from '@/graphql/hooks/usePhotosGraphQL';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';
import logger from '@/utils/logger';
import { GET_CRITIQUE_SESSION_PREVIEW } from '../../graphql/queries';
import type { TargetPreviewProps, CritiqueSessionPreviewData } from '../../types';

export function PhotoInCritiqueTargetPreview({ task }: TargetPreviewProps) {
  const photoId = task.targetContext && 'photoId' in task.targetContext
    ? String(task.targetContext.photoId)
    : null;

  // Fetch critique session data for hero image
  // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
  // from validating required variables before checking skip.
  const { data: sessionData, loading: sessionLoading } = useQuery<CritiqueSessionPreviewData>(
    GET_CRITIQUE_SESSION_PREVIEW,
    {
      variables: { id: task.targetId || 'skip-placeholder' },
      skip: !task.targetId,
      fetchPolicy: 'cache-first',
    }
  );

  // Fetch target photo data
  const { data: photoData, isLoading: photoLoading } = usePhotoByIdGraphQL(photoId ?? undefined);

  // Get hero photo from session query with fallback chain
  // Priority: systemHeroPhoto (composite) > heroPhoto (explicit) > first collection photo
  const systemHeroPhoto = sessionData?.critiqueSession?.collection?.systemHeroPhoto;
  const heroPhotoFromQuery = sessionData?.critiqueSession?.collection?.heroPhoto;
  const heroPhotoId = sessionData?.critiqueSession?.collection?.heroPhotoId;
  const firstCollectionPhoto = sessionData?.critiqueSession?.collection?.items?.items?.[0]?.photo;

  // Only fetch by ID if no photos are available from query
  const { data: heroPhotoFallback, isLoading: heroPhotoFallbackLoading } = usePhotoByIdGraphQL(
    !systemHeroPhoto && !heroPhotoFromQuery && heroPhotoId ? heroPhotoId : undefined
  );

  // Fallback chain: systemHeroPhoto > heroPhoto > heroPhotoFallback > first collection photo
  const heroPhoto = systemHeroPhoto || heroPhotoFromQuery || heroPhotoFallback || firstCollectionPhoto;
  const sessionTitle = sessionData?.critiqueSession?.title || 'Critique Session';
  const collectionTitle = sessionData?.critiqueSession?.collection?.title || '';
  const isLoading = sessionLoading || photoLoading || heroPhotoFallbackLoading;

  // Debug logging for hero photo (using info level to ensure visibility)
  if (!sessionLoading && sessionData) {
    const collection = sessionData.critiqueSession?.collection;
    logger.info('Tasks', 'PhotoInCritiqueTargetPreview data', {
      sessionTitle,
      collectionTitle,
      collectionHeroPhotoId: collection?.heroPhotoId,
      hasSystemHeroPhoto: !!systemHeroPhoto,
      hasHeroPhotoFromQuery: !!heroPhotoFromQuery,
      hasHeroPhotoFallback: !!heroPhotoFallback,
      hasFirstCollectionPhoto: !!firstCollectionPhoto,
      selectedHeroPhotoId: heroPhoto?.id,
      selectedHeroPhotoSource: systemHeroPhoto ? 'systemHeroPhoto' : heroPhotoFromQuery ? 'heroPhoto' : heroPhotoFallback ? 'heroPhotoFallback' : firstCollectionPhoto ? 'firstCollectionPhoto' : 'none',
      heroPhotoVariantsCount: heroPhoto?.variants?.length,
    });
  }

  return (
    <div className="flex items-center gap-3 p-2 bg-surface-2/50 rounded border border-border/30">
      {/* Critique Session Hero Image */}
      <div className="w-16 h-16 bg-surface-2 rounded overflow-hidden flex-shrink-0 relative">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : heroPhoto ? (
          <AdaptivePhoto
            photo={heroPhoto}
            alt={sessionTitle}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
        )}
        {/* Critique badge overlay */}
        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-surface-2/50">
          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </div>
      </div>

      {/* Photo Preview */}
      <div className="w-16 h-16 bg-surface-2 rounded overflow-hidden flex-shrink-0 relative">
        {isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : photoData?.variants ? (
          <AdaptivePhoto
            photo={photoData}
            alt="Task target photo"
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {/* Photo badge overlay */}
        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center border-2 border-surface-2/50">
          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" />
          </svg>
        </div>
      </div>

      {/* Labels */}
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted mb-0.5">Photo in Critique Session</div>
        <div className="text-sm font-medium text-foreground truncate">
          {sessionTitle}
        </div>
        {collectionTitle && (
          <div className="text-xs text-muted truncate">
            {collectionTitle}
          </div>
        )}
        {photoData?.description && (
          <div className="text-xs text-muted/70 mt-0.5 truncate">
            {photoData.description}
          </div>
        )}
      </div>
    </div>
  );
}
